"use client";

import { createContext, useState, useEffect, useContext, useRef } from "react";
import { fetchMetalRates } from "../services/api";

// Create context
const MetalRatesContext = createContext();

// Custom hook to use the metal rates context
export const useMetalRates = () => {
  const context = useContext(MetalRatesContext);
  if (!context) {
    throw new Error("useMetalRates must be used within a MetalRatesProvider");
  }
  return context;
};

// Define the cache duration (5 minutes in milliseconds)
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const MetalRatesProvider = ({ children }) => {
  const [metalRates, setMetalRates] = useState(null);
  const [previousRates, setPreviousRates] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceChanges, setPriceChanges] = useState({
    gold_24k: 0,
    gold_22k: 0,
    gold_18k: 0,
    gold_20k: 0,
    gold_14k: 0,
    silver: 0,
  });
  const [priceDirections, setPriceDirections] = useState({
    gold_24k: "neutral", // 'up', 'down', 'neutral'
    gold_22k: "neutral",
    gold_18k: "neutral",
    gold_20k: "neutral",
    gold_14k: "neutral",
    silver: "neutral",
  });

  // Timer ref to schedule exact 5-minute refreshes aligned to lastFetched
  const refreshTimeoutRef = useRef(null);

  // Helper function to safely access localStorage
  const getFromLocalStorage = (key) => {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn("localStorage access failed:", error);
      return null;
    }
  };

  const setToLocalStorage = (key, value) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn("localStorage write failed:", error);
    }
  };

  // Function to calculate price changes and directions
  // IMPORTANT: All gold directions mirror 24k's movement. If 24k is unchanged, keep existing gold colors.
  // Silver direction is based on silver's movement; if unchanged, keep existing silver color.
  const calculatePriceChanges = (currentRates, prevRates, prevDirections = null) => {
    const baseDirections = {
      gold_24k: "neutral",
      gold_22k: "neutral",
      gold_18k: "neutral",
      gold_20k: "neutral",
      gold_14k: "neutral",
      silver: "neutral",
    };
    const priorDirs = prevDirections || priceDirections || baseDirections;

    if (!currentRates || !prevRates) {
      return {
        changes: {
          gold_24k: 0,
          gold_22k: 0,
          gold_18k: 0,
          gold_20k: 0,
          gold_14k: 0,
          silver: 0,
        },
        directions: priorDirs, // keep whatever we had if we don't have both sides to compare
      };
    }

    const changes = {
      gold_24k:
        (currentRates.price_gram_24k || 0) - (prevRates.price_gram_24k || 0),
      gold_22k:
        (currentRates.price_gram_22k || 0) - (prevRates.price_gram_22k || 0),
      gold_18k:
        (currentRates.price_gram_18k || 0) - (prevRates.price_gram_18k || 0),
      gold_20k:
        (currentRates.price_gram_20k || 0) - (prevRates.price_gram_20k || 0),
      gold_14k:
        (currentRates.price_gram_14k || 0) - (prevRates.price_gram_14k || 0),
      silver:
        (currentRates.silver_price_gram_24k || 0) -
        (prevRates.silver_price_gram_24k || 0),
    };

    // Determine controller movements
    const goldControllerDelta = changes.gold_24k;
    const goldControllerDirection =
      goldControllerDelta > 0 ? "up" : goldControllerDelta < 0 ? "down" : "neutral";

    const silverDelta = changes.silver;
    const silverDirection = silverDelta > 0 ? "up" : silverDelta < 0 ? "down" : "neutral";

    // Build new directions by mirroring 24k for all gold, and using silver for silver.
    // If neutral, keep the previous color.
    const newDirections = { ...priorDirs };

    if (goldControllerDirection !== "neutral") {
      newDirections.gold_24k = goldControllerDirection;
      newDirections.gold_22k = goldControllerDirection;
      newDirections.gold_20k = goldControllerDirection;
      newDirections.gold_18k = goldControllerDirection;
      newDirections.gold_14k = goldControllerDirection;
    }
    // else: keep all gold directions as they are

    if (silverDirection !== "neutral") {
      newDirections.silver = silverDirection;
    }
    // else: keep silver direction as is

    return { changes, directions: newDirections };
  };

  // Function to check if cached data is valid
  const isCacheValid = (timestamp) => {
    if (!timestamp) return false;
    return Date.now() - parseInt(timestamp, 10) < CACHE_DURATION;
  };

  // Function to load data from cache
  const loadFromCache = () => {
    try {
      const cachedData = getFromLocalStorage("metalRates");
      const cachedTimestamp = getFromLocalStorage("metalRatesTimestamp");
      const cachedPrevious = getFromLocalStorage("previousMetalRates");
      const cachedDirections = getFromLocalStorage("priceDirections");

      if (cachedData && cachedTimestamp && isCacheValid(cachedTimestamp)) {
        const parsedData = JSON.parse(cachedData);
        const parsedPrevious = cachedPrevious ? JSON.parse(cachedPrevious) : null;
        const parsedDirections = cachedDirections ? JSON.parse(cachedDirections) : null;
        const timestamp = parseInt(cachedTimestamp, 10);

        setMetalRates(parsedData);
        setPreviousRates(parsedPrevious);
        setLastFetched(timestamp);

        if (parsedPrevious) {
          const { changes, directions } = calculatePriceChanges(
            parsedData,
            parsedPrevious,
            parsedDirections || priceDirections
          );
          setPriceChanges(changes);
          setPriceDirections(directions);
        } else if (parsedDirections) {
          setPriceDirections(parsedDirections);
        }

        return true;
      }
    } catch (error) {
      console.error("Error loading from cache:", error);
    }
    return false;
  };

  // Function to save data to cache
  const saveToCache = (data, timestamp, directions = null) => {
    try {
      setToLocalStorage("metalRates", JSON.stringify(data));
      setToLocalStorage("metalRatesTimestamp", timestamp.toString());
      if (directions) {
        setToLocalStorage("priceDirections", JSON.stringify(directions));
      }
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  // Function to fetch metal rates
  const fetchRates = async (force = false) => {
    try {
      setError(null);

      // Check cache first if not forced
      if (!force && loadFromCache()) {
        setLoading(false);
        return;
      }

      // Fetch new data
      setLoading(true);
      const data = await fetchMetalRates();

      if (data) {
        const now = Date.now();
        let newDirections = priceDirections;

        if (metalRates) {
          // Store current rates as previous before updating
          setPreviousRates(metalRates);
          setToLocalStorage("previousMetalRates", JSON.stringify(metalRates));

          // Calculate price changes and directions (gold color mirrors 24k)
          const { changes, directions } = calculatePriceChanges(
            data,
            metalRates,
            priceDirections
          );
          setPriceChanges(changes);
          setPriceDirections(directions);
          newDirections = directions;
        }

        // Update with new rates
        setMetalRates(data);
        setLastFetched(now);

        // Save to cache
        saveToCache(data, now, newDirections);
      } else {
        throw new Error("No data received from API");
      }
    } catch (error) {
      console.error("Error fetching metal rates:", error);
      setError(error.message);

      // Try to load from cache as fallback
      if (!metalRates) {
        loadFromCache();
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    const initialize = async () => {
      const cacheLoaded = loadFromCache();

      if (!cacheLoaded) {
        await fetchRates();
      } else {
        setLoading(false);
      }
    };

    initialize();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Schedule exact 5-minute refreshes aligned to lastFetched (displayed time)
  useEffect(() => {
    if (!lastFetched) return;

    // Clear any existing timer
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    const now = Date.now();
    const remaining = Math.max(0, CACHE_DURATION - (now - lastFetched));

    refreshTimeoutRef.current = setTimeout(() => {
      fetchRates();
    }, remaining);

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [lastFetched]); // reschedule on every successful fetch/cached load

  // Calculate time until next refresh
  const getTimeUntilNextRefresh = () => {
    if (!lastFetched) return 0;
    const elapsed = Date.now() - lastFetched;
    const remaining = Math.max(0, CACHE_DURATION - elapsed);
    return Math.ceil(remaining / 1000 / 60); // minutes remaining
  };

  // Format last updated time
  const getLastUpdatedFormatted = () => {
    if (!lastFetched) return "Never";
    return new Date(lastFetched).toLocaleString("en-IN", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const contextValue = {
    metalRates,
    previousRates,
    priceChanges,
    priceDirections, // gold colors mirror 24k; silver color mirrors silver
    loading,
    error,
    lastFetched,
    lastUpdatedFormatted: getLastUpdatedFormatted(),
    timeUntilNextRefresh: getTimeUntilNextRefresh(),
    refreshRates: () => fetchRates(true), // Force refresh function
    isDataStale: () => {
      const timestamp = getFromLocalStorage("metalRatesTimestamp");
      return !timestamp || !isCacheValid(timestamp);
    },
  };

  return (
    <MetalRatesContext.Provider value={contextValue}>
      {children}
    </MetalRatesContext.Provider>
  );
};

export default MetalRatesProvider;